<template>
  <div>
    <KtDatatable
      :model="columns"
      :data="rows"
      :filter="{ ...filter, total }"
      :update-function="updateFilter"
      :hide-search="false"
      storage-key="job-documents"
      @cell-click="handleCellClick"
    >
      <template #view_string="scope">
        <span v-if="scope.column.name === 'AccessMode'" class="badge" :class="accessBadge(scope.row.AccessMode)">{{ accessLabel(scope.row.AccessMode) }}</span>
        <span v-else-if="scope.column.name === 'Status'" class="badge" :class="scope.row.Status & 1 ? 'bg-success' : 'bg-secondary'">{{ scope.row.Status & 1 ? 'Submitted' : 'Draft' }}</span>
        <span v-else>{{ scope.value }}</span>
      </template>
      <template #view_date="scope">{{ formatDate(scope.value) }}</template>
      <template #view_actions="scope">
        <div class="d-flex gap-1">
          <button class="btn btn-sm btn-outline-primary" title="Open" @click.stop="openDocument(scope.row.Id)"><i class="fa fa-edit"></i></button>
          <button v-if="!disableActions" class="btn btn-sm btn-outline-danger" title="Remove" @click.stop="removeDocument(scope.row as FormSubmissionModel)"><i class="fa fa-trash"></i></button>
        </div>
      </template>
      <template #after-search>
        <div class="d-flex gap-2">
          <button class="btn btn-sm btn-outline-secondary" @click="load">Refresh</button>
          <button v-if="!disableActions" class="btn btn-sm btn-outline-primary" @click="openCreate">New <i class="fa fa-plus"></i></button>
        </div>
      </template>
    </KtDatatable>

    <KtModal :visible="showCreate" title="New Job Document" @dismiss="showCreate = false">
      <template #body>
        <div class="mb-3">
          <label class="form-label">Form Template</label>
          <select v-model="createModel.TemplateId" class="form-select" @change="useTemplateDefaults">
            <option value="">Select a template</option>
            <option v-for="template in templates" :key="template.Id" :value="template.Id">{{ template.Title }}</option>
          </select>
        </div>
        <div class="mb-3">
          <label class="form-label">Name</label>
          <input v-model="createModel.DocumentName" class="form-control" />
        </div>
        <div class="mb-3">
          <label class="form-label">Description</label>
          <textarea v-model="createModel.DocumentDescription" class="form-control" rows="3"></textarea>
        </div>
        <div class="mb-3">
          <label class="form-label">Access</label>
          <select v-model.number="createModel.AccessMode" class="form-select">
            <option :value="0">Private</option>
            <option :value="1">Secure link without sign-in</option>
            <option :value="2">Secure link requiring sign-in</option>
          </select>
        </div>
        <div v-if="createModel.AccessMode !== 0" class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Expires</label>
            <input v-model="createModel.ExpiresAt" type="datetime-local" class="form-control" />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Maximum Uses</label>
            <input v-model.number="createModel.MaxUses" type="number" min="1" class="form-control" placeholder="Unlimited" />
          </div>
        </div>
      </template>
      <template #footer>
        <button class="btn btn-secondary" @click="showCreate = false">Cancel</button>
        <button class="btn btn-primary" :disabled="saving || !createModel.TemplateId || !createModel.DocumentName.trim()" @click="createDocument">Save</button>
      </template>
    </KtModal>

    <KtModal :visible="showEdit" :title="editing?.DocumentName || 'Document'" size="modal-xl" @dismiss="closeEdit">
      <template #body>
        <div v-if="editing">
          <div class="row mb-3">
            <div class="col-md-4">
              <label class="form-label">Name</label>
              <input v-model="editing.DocumentName" class="form-control" :disabled="disableActions" />
            </div>
            <div class="col-md-4">
              <label class="form-label">Description</label>
              <input v-model="editing.DocumentDescription" class="form-control" :disabled="disableActions" />
            </div>
            <div class="col-md-4">
              <label class="form-label">Status</label>
              <select v-model="editingStatus" class="form-select" :disabled="disableActions">
                <option value="Active">Active</option>
                <option value="Submitted">Submitted</option>
              </select>
            </div>
          </div>
          <FormRenderer v-if="editingStructure" :key="editing.Id" :form-structure="editingStructure" :initial-answers="editingAnswers" @update="editingAnswers = { ...$event }" @submit="submitRenderedDocument" />
          <div v-if="publicUrl" class="input-group mt-3">
            <input class="form-control" :value="publicUrl" readonly />
            <button class="btn btn-outline-secondary" @click="copyPublicUrl">Copy Link</button>
          </div>
        </div>
      </template>
      <template #footer>
        <button class="btn btn-outline-secondary me-auto" @click="openHistory">History</button>
        <button v-if="!disableActions" class="btn btn-outline-primary" @click="openAccess">Access</button>
        <button class="btn btn-secondary" @click="closeEdit">Close</button>
        <button v-if="!disableActions" class="btn btn-outline-primary" :disabled="saving" @click="saveDocument(false)">Save Draft</button>
        <button v-if="!disableActions" class="btn btn-primary" :disabled="saving" @click="saveDocument(true)">Submit</button>
      </template>
    </KtModal>

    <KtModal :visible="showAccess" title="Document Access" @dismiss="showAccess = false">
      <template #body>
        <div class="mb-3">
          <label class="form-label">Access</label>
          <select v-model.number="accessModel.AccessMode" class="form-select">
            <option :value="0">Private</option>
            <option :value="1">Secure link without sign-in</option>
            <option :value="2">Secure link requiring sign-in</option>
          </select>
        </div>
        <div v-if="accessModel.AccessMode !== 0" class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Expires</label>
            <input v-model="accessModel.ExpiresAt" type="datetime-local" class="form-control" />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Maximum Uses</label>
            <input v-model.number="accessModel.MaxUses" type="number" min="1" class="form-control" placeholder="Unlimited" />
          </div>
          <div class="col-12 form-check ms-2">
            <input id="allow-document-editing" v-model="accessModel.AllowEditing" type="checkbox" class="form-check-input" />
            <label for="allow-document-editing" class="form-check-label">Allow editing after submission</label>
          </div>
        </div>
      </template>
      <template #footer>
        <button class="btn btn-secondary" @click="showAccess = false">Cancel</button>
        <button class="btn btn-primary" :disabled="saving" @click="saveAccess">Apply</button>
      </template>
    </KtModal>

    <KtModal :visible="showHistory" title="Revision History" size="modal-xl" @dismiss="showHistory = false">
      <template #body>
        <div class="row">
          <div class="col-md-4">
            <div class="list-group">
              <button v-for="revision in revisions" :key="revision.Id" type="button" class="list-group-item list-group-item-action" :class="{ active: selectedRevision?.Id === revision.Id }" @click="selectRevision(revision)">
                <div class="fw-semibold">Revision {{ revision.RevisionNumber }}</div>
                <small>{{ revision.EditorName || revision.EditorEmail || sourceLabel(revision.EditSource) }} · {{ formatDate(revision.DateCreated) }}</small>
              </button>
            </div>
          </div>
          <div class="col-md-8 history-renderer" v-if="selectedRevision && editingStructure">
            <FormRenderer :key="selectedRevision.Id" :form-structure="editingStructure" :initial-answers="revisionAnswers" />
          </div>
        </div>
      </template>
      <template #footer><button class="btn btn-secondary" @click="showHistory = false">Close</button></template>
    </KtModal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import Api, { type FormSubmissionModel, type FormSubmissionRevisionModel, type FormTemplateModel } from '@/services/ApiService';
import KtDatatable from '@/components/UI/KtDatatable.vue';
import KtModal from '@/components/UI/KtModal.vue';
import FormRenderer from '@/components/FormBuilder/FormRenderer.vue';
import { normalizeFormStructure } from '@/components/utils/form-helpers';
import type { FormAnswer, FormStructure } from '@/components/types/form-builder';

const props = defineProps({
  requestId: { type: [Number, String], required: true },
  companyId: { type: [Number, String], default: null },
  disableActions: { type: Boolean, default: false },
});
const emit = defineEmits<{ (e: 'count-loaded', count: number): void }>();

const rows = ref<FormSubmissionModel[]>([]);
const total = ref(0);
const filter = ref({ Page: 0, Size: 25, SearchTerm: '' });
const templates = ref<FormTemplateModel[]>([]);
const saving = ref(false);
const showCreate = ref(false);
const showEdit = ref(false);
const showAccess = ref(false);
const showHistory = ref(false);
const editing = ref<FormSubmissionModel | null>(null);
const editingStructure = ref<FormStructure | null>(null);
const editingAnswers = ref<FormAnswer>({});
const editingStatus = ref<'Active' | 'Submitted'>('Active');
const revisions = ref<FormSubmissionRevisionModel[]>([]);
const selectedRevision = ref<FormSubmissionRevisionModel | null>(null);
const revisionAnswers = ref<FormAnswer>({});
const publicUrl = ref('');
const createModel = ref(emptyCreate());
const accessModel = ref({ AccessMode: 0, ExpiresAt: '', MaxUses: undefined as number | undefined, AllowEditing: true });

const columns = [
  { name: 'DocumentName', label: 'Name', type: 'string', display: true, sortable: true },
  { name: 'DocumentDescription', label: 'Description', type: 'string', display: true },
  { name: 'TemplateName', label: 'Template', type: 'string', display: true },
  { name: 'Status', label: 'Status', type: 'string', display: true },
  { name: 'AccessMode', label: 'Access', type: 'string', display: true },
  { name: 'DateModified', label: 'Last Updated', type: 'date', display: true, sortable: true },
  { name: 'Actions', label: '', type: 'actions', display: true, width: 90 },
];

function emptyCreate() {
  return { TemplateId: '', DocumentName: '', DocumentDescription: '', AccessMode: 0, ExpiresAt: '', MaxUses: undefined as number | undefined, AllowEditing: true };
}

async function load() {
  const response = await Api.loadJobDocuments(Number(props.requestId), filter.value, true);
  rows.value = (response.Data || []).map(row => ({ ...row, TemplateName: row.Template?.Title } as FormSubmissionModel));
  total.value = response.Meta?.Total || 0;
  emit('count-loaded', total.value);
}

async function updateFilter(value: any) {
  filter.value = { ...filter.value, ...value };
  await load();
}

async function openCreate() {
  createModel.value = emptyCreate();
  const response = await Api.loadFormTemplates({ IsActive: true, Page: 0, Size: 200 }, true);
  templates.value = response.Data || [];
  showCreate.value = true;
}

function useTemplateDefaults() {
  const template = templates.value.find(item => item.Id === createModel.value.TemplateId);
  if (!template) return;
  if (!createModel.value.DocumentName) createModel.value.DocumentName = template.Title;
  if (!createModel.value.DocumentDescription) createModel.value.DocumentDescription = template.Description || '';
}

async function createDocument() {
  saving.value = true;
  try {
    const result = await Api.createJobDocument({
      RequestId: Number(props.requestId),
      CompanyId: props.companyId ? Number(props.companyId) : undefined,
      TemplateId: createModel.value.TemplateId,
      DocumentName: createModel.value.DocumentName,
      DocumentDescription: createModel.value.DocumentDescription || undefined,
      AccessMode: createModel.value.AccessMode,
      ExpiresAt: createModel.value.ExpiresAt ? new Date(createModel.value.ExpiresAt).toISOString() : undefined,
      MaxUses: createModel.value.MaxUses,
      AllowEditing: createModel.value.AllowEditing,
    });
    showCreate.value = false;
    await load();
    await openDocument(result.Document.Id!);
    publicUrl.value = result.PublicUrl || '';
  } finally {
    saving.value = false;
  }
}

async function openDocument(id: string) {
  const document = await Api.getJobDocument(id, true);
  editing.value = document;
  editingStructure.value = normalizeFormStructure(JSON.parse(document.TemplateSchemaSnapshot || document.Template?.FormSchema || '{}'));
  editingAnswers.value = JSON.parse(document.FormData || '{}');
  editingStatus.value = (document.Status & 1) ? 'Submitted' : 'Active';
  publicUrl.value = '';
  showEdit.value = true;
}

function closeEdit() {
  showEdit.value = false;
  editing.value = null;
  editingStructure.value = null;
  editingAnswers.value = {};
}

function submitRenderedDocument(answers: FormAnswer) {
  if (props.disableActions) return;
  editingAnswers.value = { ...answers };
  saveDocument(true);
}

async function saveDocument(submit: boolean) {
  if (!editing.value?.Id) return;
  saving.value = true;
  try {
    editing.value = await Api.saveJobDocument(editing.value.Id, {
      FormData: JSON.stringify(editingAnswers.value),
      DocumentName: editing.value.DocumentName,
      DocumentDescription: editing.value.DocumentDescription,
      Status: editingStatus.value === 'Submitted' ? 1 : 0,
      Submit: submit,
    });
    await load();
  } finally {
    saving.value = false;
  }
}

function openAccess() {
  if (!editing.value) return;
  accessModel.value = { AccessMode: editing.value.AccessMode || 0, ExpiresAt: '', MaxUses: undefined, AllowEditing: true };
  showAccess.value = true;
}

async function saveAccess() {
  if (!editing.value?.Id) return;
  saving.value = true;
  try {
    const result = await Api.setJobDocumentAccess(editing.value.Id, {
      AccessMode: accessModel.value.AccessMode,
      ExpiresAt: accessModel.value.ExpiresAt ? new Date(accessModel.value.ExpiresAt).toISOString() : undefined,
      MaxUses: accessModel.value.MaxUses,
      AllowEditing: accessModel.value.AllowEditing,
    });
    editing.value = result.Document;
    publicUrl.value = result.PublicUrl || '';
    showAccess.value = false;
    await load();
  } finally {
    saving.value = false;
  }
}

async function openHistory() {
  if (!editing.value?.Id) return;
  revisions.value = await Api.loadJobDocumentRevisions(editing.value.Id, true);
  selectedRevision.value = revisions.value[0] || null;
  revisionAnswers.value = selectedRevision.value ? JSON.parse(selectedRevision.value.FormData || '{}') : {};
  showHistory.value = true;
}

function selectRevision(revision: FormSubmissionRevisionModel) {
  selectedRevision.value = revision;
  revisionAnswers.value = JSON.parse(revision.FormData || '{}');
}

function handleCellClick(row: FormSubmissionModel, column: any) {
  if (column.name !== 'Actions') openDocument(row.Id!);
}

async function removeDocument(document: FormSubmissionModel) {
  if (!document.Id || !window.confirm(`Remove ${document.DocumentName || 'this document'}?`)) return;
  await Api.deleteJobDocument(document.Id);
  await load();
}

function formatDate(value?: string) { return value ? new Date(value).toLocaleString() : ''; }
function accessLabel(value?: number) { return value === 1 ? 'Public link' : value === 2 ? 'Sign-in link' : 'Private'; }
function accessBadge(value?: number) { return value === 1 ? 'bg-warning text-dark' : value === 2 ? 'bg-info text-dark' : 'bg-secondary'; }
function sourceLabel(value: number) { return value === 1 ? 'Anonymous link' : value === 2 ? 'Authenticated link' : 'Internal'; }
async function copyPublicUrl() { if (publicUrl.value) await navigator.clipboard.writeText(publicUrl.value); }

watch(() => props.requestId, load);
onMounted(load);
</script>

<style scoped>
.history-renderer { pointer-events: none; opacity: 0.9; }
</style>
